import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, XCircle, HandCoins, ArrowLeftRight, Undo2, Loader2 } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = {
  pending: { label: "Lopend", cls: "text-[#FFB27A]" },
  accepted: { label: "Geaccepteerd", cls: "text-[#22C55E]" },
  rejected: { label: "Afgewezen", cls: "text-[#EF4444]" },
  withdrawn: { label: "Ingetrokken", cls: "text-[#777]" },
};

// One offer/negotiation, with turn-aware actions. Used by both the seller
// (per-listing offers) and the buyer ("Mijn biedingen").
export function OfferCard({ offer, role, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [counterOpen, setCounterOpen] = useState(false);

  const amount = offer.current_amount ?? offer.amount;
  const pending = offer.status === "pending";
  const myTurn = pending && offer.turn === role;
  const lastMove = offer.history?.[offer.history.length - 1];
  const st = STATUS[offer.status] || STATUS.pending;

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${offer.id}/${action}`);
      if (action === "accept") {
        toast.success("Bod geaccepteerd", {
          action: data.conversation_id ? { label: "Open chat", onClick: () => navigate(`/chats/${data.conversation_id}`) } : undefined,
        });
      } else if (action === "reject") toast.success("Bod afgewezen");
      else if (action === "withdraw") toast.success("Bod ingetrokken");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };

  const doCounter = async (value) => {
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/counter`, { amount: value });
      toast.success("Tegenbod verstuurd");
      setCounterOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Tegenbod mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`offer-row-${offer.id}`} className="rounded-lg border border-[#333] bg-[#111] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-xl text-[#FF6A00]">{formatPrice(amount)}</p>
          <p className="truncate text-xs text-[#A0A0A0]">
            {role === "seller" ? offer.bidder_name : offer.listing_title} · <span className={st.cls}>{st.label}</span>
          </p>
          {pending && (
            <p className="mt-0.5 text-xs text-[#777]">
              {myTurn ? "Jouw beurt om te reageren" : "In afwachting van reactie"}
              {lastMove ? ` · laatste: ${lastMove.by === "seller" ? "verkoper" : "koper"} ${formatPrice(lastMove.amount)}` : ""}
            </p>
          )}
        </div>
        {pending && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {myTurn && (
              <>
                <button disabled={busy} onClick={() => act("accept")} data-testid={`offer-accept-${offer.id}`} className="flex items-center gap-1 rounded-full bg-[#22C55E] px-3 py-1.5 text-xs font-bold text-[#111] disabled:opacity-60"><Check className="h-3.5 w-3.5" /> Accepteer</button>
                <button disabled={busy} onClick={() => setCounterOpen(true)} data-testid={`offer-counter-${offer.id}`} className="flex items-center gap-1 rounded-full border border-[#FF6A00] px-3 py-1.5 text-xs font-bold text-[#FF6A00] disabled:opacity-60"><ArrowLeftRight className="h-3.5 w-3.5" /> Tegenbod</button>
                <button disabled={busy} onClick={() => act("reject")} data-testid={`offer-reject-${offer.id}`} className="flex items-center gap-1 rounded-full border border-[#EF4444]/50 px-3 py-1.5 text-xs font-bold text-[#EF4444] disabled:opacity-60"><XCircle className="h-3.5 w-3.5" /> Wijs af</button>
              </>
            )}
            {role === "buyer" && (
              <button disabled={busy} onClick={() => act("withdraw")} data-testid={`offer-withdraw-${offer.id}`} className="flex items-center gap-1 rounded-full border border-[#333] px-3 py-1.5 text-xs font-semibold text-[#A0A0A0] disabled:opacity-60"><Undo2 className="h-3.5 w-3.5" /> Intrekken</button>
            )}
          </div>
        )}
      </div>
      <CounterDialog open={counterOpen} onClose={() => setCounterOpen(false)} current={amount} busy={busy} onSubmit={doCounter} />
    </div>
  );
}

function CounterDialog({ open, onClose, current, busy, onSubmit }) {
  const [val, setVal] = useState("");
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="counter-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><ArrowLeftRight className="h-6 w-6 text-[#FF6A00]" /> TEGENBOD</DialogTitle></DialogHeader>
        <p className="text-sm text-[#A0A0A0]">Huidig bod: <span className="font-semibold text-[#F5F5F5]">{formatPrice(current)}</span></p>
        <div className="relative py-2">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#777]">€</span>
          <input autoFocus type="number" min="1" inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Jouw tegenbod" data-testid="counter-amount-input" className="w-full rounded-lg border border-[#333] bg-[#2A2A2A] py-3 pl-8 pr-3 text-lg text-[#F5F5F5] outline-none focus:border-[#FF6A00]" />
        </div>
        <button disabled={busy} onClick={() => { const v = parseFloat(val); if (!v || v <= 0) return toast.error("Voer een geldig bedrag in"); onSubmit(v); }} data-testid="counter-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3 font-bold text-[#111] disabled:opacity-60">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Verstuur tegenbod</button>
      </DialogContent>
    </Dialog>
  );
}
